import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { pluralizeToString } from 'lib/string/pluralize'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getPropertyCombinedRatesByRoom,
  getPropertyMappedBedbankOffer,
} from 'selectors/offerPage/offerPageSelectors'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Pane from 'components/Common/Pane'
import PropertyRoomCheapestOptionMobile from 'components/OfferPage/Property/MobileBookingFlow/PropertyRoomCheapestOptionMobile'
import type { RoomRateViews } from 'components/OfferPage/Property/singlePropertyUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import AlwaysOnMobileStickyHeaderFilters from '../AlwaysOnMobileStickyHeaderFilters'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
`

interface Props {
  offer: App.HotelOffer;
  bedbankOffer?: App.BedbankOffer;
  combinedRatesByRoom: Array<RoomRateViews>;
  onBack?: () => void;
  onClose: () => void;
}

function SinglePropertyRoomSelectionModal(props: Props) {
  const {
    offer,
    bedbankOffer,
    combinedRatesByRoom,
    onBack,
    onClose,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao or Robert.                                                              */
  /************************************************************************************************/

  const highlightedPackage = useMemo(
    () => combinedRatesByRoom.find(({ isHighlighted }) => isHighlighted),
    [combinedRatesByRoom],
  )

  return (
    <>
      <ModalHeader
        title="Select a room"
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <AlwaysOnMobileStickyHeaderFilters />
      </ModalHeader>
      <ModalBody>
        <>
          {combinedRatesByRoom.length > 1 && (
            <OptionsContainer>
              <BodyText variant="small" colour="neutral-three">
                {pluralizeToString('room type', combinedRatesByRoom.length)} available
              </BodyText>
            </OptionsContainer>
          )}
          <Pane background="neutral-six">
            <VerticalSpacer gap={8}>
              {highlightedPackage && !!highlightedPackage.displayRates.length && <PropertyRoomCheapestOptionMobile
                offer={offer}
                bedbankOffer={bedbankOffer}
                propertyRoom={highlightedPackage.propertyRoom}
                availableRooms={highlightedPackage.availableRooms}
                displayRates={highlightedPackage.displayRates}
                roomFitsCapacity={highlightedPackage.roomFitsCapacity}
                datesAvailable={highlightedPackage.datesAvailable}
                isHighlighted
                roomRateUnmatched={highlightedPackage.roomRateUnmatched}
                detailsModalMode="room-only"
              />}
              {combinedRatesByRoom.map((item) => {
                if (highlightedPackage && highlightedPackage.roomId === item.roomId) {
                  return null
                }
                return (<PropertyRoomCheapestOptionMobile
                  key={item.roomId}
                  offer={offer}
                  bedbankOffer={bedbankOffer}
                  propertyRoom={item.propertyRoom}
                  availableRooms={item.availableRooms}
                  displayRates={item.displayRates}
                  roomFitsCapacity={item.roomFitsCapacity}
                  datesAvailable={item.datesAvailable}
                  detailsModalMode="room-only"
                />)
              })}
            </VerticalSpacer>
          </Pane>
        </>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  offer: state.offer,
  bedbankOffer: getPropertyMappedBedbankOffer(state, appState),
  combinedRatesByRoom: getPropertyCombinedRatesByRoom(state, appState),
}), SinglePropertyRoomSelectionModal)
